/**
 * Platform detection and service factory.
 *
 * Detects whether the app is running on web or native (Capacitor) and
 * returns the appropriate platform service implementations.
 *
 * Usage:
 * ```ts
 * const services = createPlatformServices();
 * await services.secureStorage.store('key', 'value');
 * ```
 *
 * @module platform/index
 */

import type { PlatformServices } from './types';
import { createWebSecureStorage } from './secure-storage.web';
import { createWebBiometricAuth } from './biometrics.web';
import { createWebPushNotifications } from './push.web';

export type {
  SecureStorage,
  BiometricAuth,
  PushNotifications,
  PlatformCapabilities,
  PlatformServices,
} from './types';

export { detectCapabilities, checkMinimumCapabilities } from './capabilities';

/**
 * Detect if the app is running on a native platform (iOS/Android via Capacitor).
 *
 * @returns true if running in a Capacitor native shell
 */
export function isNativePlatform(): boolean {
  try {
    // Capacitor injects a global object when running in native shell
    const win = typeof window !== 'undefined' ? window : undefined;
    if (!win) return false;

    const capacitor = (win as unknown as Record<string, unknown>)['Capacitor'] as
      | { isNativePlatform?: () => boolean }
      | undefined;
    return capacitor?.isNativePlatform?.() ?? false;
  } catch {
    return false;
  }
}

/**
 * Create web platform services.
 *
 * @returns PlatformServices with web implementations
 */
function createWebServices(): PlatformServices {
  return {
    secureStorage: createWebSecureStorage(),
    biometrics: createWebBiometricAuth(),
    push: createWebPushNotifications(),
  };
}

/**
 * Create native platform services (Capacitor).
 * Uses dynamic imports to avoid loading native plugins on web.
 *
 * @returns PlatformServices with native implementations
 */
async function createNativeServices(): Promise<PlatformServices> {
  const [
    { createNativeSecureStorage },
    { createNativeBiometricAuth },
    { createNativePushNotifications },
  ] = await Promise.all([
    import('./secure-storage.native'),
    import('./biometrics.native'),
    import('./push.native'),
  ]);

  return {
    secureStorage: createNativeSecureStorage(),
    biometrics: createNativeBiometricAuth(),
    push: createNativePushNotifications(),
  };
}

/**
 * Create platform services based on detected platform.
 *
 * Returns web implementations by default. On Capacitor native platforms,
 * dynamically loads and returns native implementations.
 *
 * @returns Promise resolving to PlatformServices for the current platform
 */
export async function createPlatformServices(): Promise<PlatformServices> {
  if (isNativePlatform()) {
    return createNativeServices();
  }
  return createWebServices();
}
